import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BlogPostModel } from "../services/airtable/models/blogPost";
import { Navbar11 } from "./components/Navbar11";
import { Footer } from "../components/shared/Footer";

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        const posts = await BlogPostModel.getAllPublished();
        const foundPost = posts.find(p => p.slug === slug);
        
        if (!foundPost) {
          setError('Post not found');
          return;
        }

        setPost(foundPost);
      } catch (err) {
        console.error('Error loading blog post:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="container mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2 mb-12"></div>
            <div className="h-64 bg-gray-700 rounded mb-8"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-700 rounded w-full"></div>
              <div className="h-4 bg-gray-700 rounded w-full"></div>
              <div className="h-4 bg-gray-700 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Oops!</h1>
          <p className="text-xl mb-8">{error}</p>
          <button 
            onClick={() => navigate('/blog')}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return to Blog
          </button>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar11 />
      <article className="container mx-auto px-4 py-16">
        {/* Header */}
        <header className="max-w-4xl mx-auto mb-16">
          <div className="text-sm text-muted-foreground mb-4">
            {post.category} • {new Date(post.publishDate).toLocaleDateString()}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{post.title}</h1>
          <p className="text-xl text-muted-foreground mb-8">{post.subtitle}</p>
          <div className="flex items-center">
            <div className="text-sm">
              <span className="font-semibold">{post.author}</span>
              <span className="text-muted-foreground"> • {post.readTimeMinutes} min read</span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {post.titleImageUrl && (
          <div className="max-w-5xl mx-auto mb-16">
            <img
              src={post.titleImageUrl}
              alt={post.title}
              className="w-full h-auto rounded-lg shadow-lg"
            />
          </div>
        )}

        {/* Content */}
        <div className="max-w-4xl mx-auto prose prose-invert prose-lg">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              // Override default element styling
              h1: ({node, ...props}) => <h1 className="text-4xl font-bold mt-8 mb-4" {...props} />,
              h2: ({node, ...props}) => <h2 className="text-3xl font-bold mt-8 mb-4" {...props} />,
              h3: ({node, ...props}) => <h3 className="text-2xl font-bold mt-6 mb-3" {...props} />,
              p: ({node, ...props}) => <p className="mb-4 leading-relaxed" {...props} />,
              a: ({node, ...props}) => (
                <a 
                  className="text-primary hover:text-primary/80 underline" 
                  target="_blank"
                  rel="noopener noreferrer"
                  {...props} 
                />
              ),
              ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4" {...props} />,
              ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4" {...props} />,
              li: ({node, ...props}) => <li className="mb-2" {...props} />,
              blockquote: ({node, ...props}) => (
                <blockquote 
                  className="border-l-4 border-primary pl-4 italic my-4" 
                  {...props} 
                />
              ),
              code: ({node, inline, ...props}) => (
                inline 
                  ? <code className="bg-gray-800 rounded px-1 py-0.5" {...props} />
                  : <code className="block bg-gray-800 rounded p-4 my-4 overflow-x-auto" {...props} />
              ),
              img: ({node, ...props}) => (
                <img 
                  className="rounded-lg shadow-lg my-8 mx-auto" 
                  {...props} 
                />
              ),
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="max-w-4xl mx-auto mt-16">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
}
